import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "./api";
import StaffSessionLock from "../components/StaffSessionLock";
import { beginStaffActivity, clearStaffActivity, hasStaffActivity, idleExpired, isStaff, recordStaffActivity } from "./staffIdle";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => {
    if (!isStaff(user)) return;
    if (!hasStaffActivity()) beginStaffActivity(user);
    let pending = false;
    let sending = false;
    const lock = () => setLocked(true);
    const checkDeadline = () => { if (idleExpired()) lock(); };
    const interact = event => {
      if (locked || !event.isTrusted || document.visibilityState === "hidden") return;
      if (!recordStaffActivity()) { lock(); return; }
      pending = true;
    };
    const events = ["pointerdown", "keydown", "wheel", "touchstart"];
    events.forEach(name => window.addEventListener(name, interact, { passive: true }));
    window.addEventListener("mg-session-expired", lock);
    window.addEventListener("focus", checkDeadline);
    document.addEventListener("visibilitychange", checkDeadline);
    const timer = setInterval(async () => {
      if (locked) return;
      if (idleExpired()) { lock(); return; }
      if (!pending || sending) return;
      pending = false;
      sending = true;
      try { await api.post("/auth/activity"); }
      catch (error) { if (error?.response?.status === 401) lock(); }
      finally { sending = false; }
    }, 1000);
    return () => {
      clearInterval(timer);
      events.forEach(name => window.removeEventListener(name, interact));
      window.removeEventListener("mg-session-expired", lock);
      window.removeEventListener("focus", checkDeadline);
      document.removeEventListener("visibilitychange", checkDeadline);
    };
  }, [user, locked]);

  useEffect(() => {
    if (!locked) return;
    const warn = event => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [locked]);

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch (e) {
      if (isStaff(userRef.current)) {
        // A failed refresh must not unmount an existing unsaved staff form.
        if (e?.response?.status === 401 || idleExpired()) setLocked(true);
      } else {
        clearStaffActivity();
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, workspace = "clinical") => {
    sessionStorage.removeItem("mg_branch_id");
    const r = await api.post(workspace === "pharmacy" ? "/v1/pharmacy/auth/login" : "/auth/login", { email, password });
    sessionStorage.setItem("mg_token", r.data.token);
    beginStaffActivity(r.data.user);
    setLocked(false);
    setUser(r.data.user);
    return r.data.user;
  };

  const registerClinic = async (payload) => {
    const r = await api.post("/auth/register-clinic", payload);
    return r.data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      void error;
    }
    sessionStorage.removeItem("mg_token");
    sessionStorage.removeItem("mg_branch_id");
    clearStaffActivity();
    setLocked(false);
    setUser(null);
    window.location.href = "/";
  };

  const selectBranch = async (branchId) => {
    sessionStorage.setItem("mg_branch_id", branchId);
    await checkAuth();
  };

  const resume = async password => {
    // Never expose the preserved form to a different identity or role.
    if (user.impersonated_by) throw new Error("Sign out and sign in as the administrator to resume an impersonated session.");
    const r = await api.post(user.workspace === "pharmacy" ? "/v1/pharmacy/auth/login" : "/auth/login", { email: user.email, password });
    if (r.data.user.user_id !== user.user_id || r.data.user.role !== user.role || r.data.user.clinic_id !== user.clinic_id || (r.data.user.workspace || "clinical") !== (user.workspace || "clinical")) {
      throw new Error("Your account access changed. Sign out and sign in again.");
    }
    sessionStorage.setItem("mg_token", r.data.token);
    beginStaffActivity(user);
    setLocked(false);
  };

  const stopImpersonation = async () => {
    try {
      await api.post("/admin/impersonate/stop");
    } catch (error) {
      void error;
    }
    await checkAuth();
    window.location.href = "/super-admin";
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, registerClinic, logout, checkAuth, selectBranch, stopImpersonation }}>
      <div hidden={locked} inert={locked}>{children}</div>
      {locked && user && <StaffSessionLock user={user} resume={resume} logout={logout} />}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function roleHome(role) {
  switch (role) {
    case "super_admin": return "/super-admin";
    case "clinic_admin":
    case "nurse":
    case "billing_staff":
    case "lab_staff":
    case "support_staff":
      return "/clinic-admin";
    case "clinic_listing":
    case "pharmacy_listing":
    case "private_practitioner": return "/listing-panel";
    case "doctor": return "/doctor";
    case "receptionist": return "/reception";
    case "patient": return "/patient";
    case "pharmacy_admin":
    case "pharmacist":
    case "pharmacy_operations": return "/pharmacy-panel";
    default: return "/";
  }
}
